import React, { createContext, useState, useContext, useEffect } from 'react';
import authService from '../services/authService';

// Create the auth context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in on component mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // Check if user is authenticated
        if (authService.isAuthenticated()) {
          // Get the current user from localStorage
          const userData = authService.getCurrentUser();
          
          // Optionally verify token on the backend
          // const tokenVerification = await authService.getProfile();
          
          setUser(userData);
        }
      } catch (err) {
        console.error('Auth status check error:', err);
        setError('Failed to check authentication status');
        // Clear invalid auth data
        authService.logout();
      } finally {
        setLoading(false);
      }
    };
    
    checkAuthStatus();
  }, []);

  // Register a new user
  const register = async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      // Use the authService to register the user
      const data = await authService.register(userData);
      
      // Update local state
      setUser(authService.getCurrentUser());
      
      return data;
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Login an existing user
  const login = async (credentials) => {
    setLoading(true);
    setError(null);
    
    try {
      // Use the authService to login the user
      const data = await authService.login(credentials);
      
      // Update local state
      setUser(authService.getCurrentUser());
      
      return data;
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout the current user
  const logout = () => {
    // Use the authService to logout the user
    authService.logout();
    
    // Update state
    setUser(null);
  };

  // Update the user's profile
  const updateProfile = async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      // Use the authService to update the profile
      const data = await authService.updateProfile(userData);
      
      // Update local state with new user data
      setUser(authService.getCurrentUser());
      
      return data;
    } catch (err) {
      console.error('Profile update error:', err);
      setError(err.message || 'Profile update failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get authentication token
  const getToken = () => {
    return localStorage.getItem('wastenet_token');
  };
  
  // Check if user has a specific role
  const hasRole = (role) => {
    return user && user.role === role;
  };
  
  // Check if user is an admin
  const isAdmin = () => hasRole('admin');
  
  // Check if user is a donor
  const isDonor = () => hasRole('donor');
  
  // Check if user is a recipient
  const isRecipient = () => hasRole('recipient');

  // Context value
  const value = {
    user,
    loading,
    error,
    register,
    login,
    logout,
    updateProfile,
    getToken,
    hasRole,
    isAdmin,
    isDonor,
    isRecipient,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
